/**
 * Mock data for Seat utilization detail (Operations).
 * Replace with API when ready.
 */

import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import {
  getOperationsSeatUtilizationMetrics,
  OPERATIONS_SEAT_DONUT_SLICES,
} from '@components/pages/Dashboard/Operations/operationsMockData'
import type { SchedulingChartConfig } from '../SchedulingPerformanceDetail/schedulingPerformanceDetail.mockData'
import type { ExpenseCategoryBarConfig } from '../ExpensesBreakdownDetail/expensesBreakdownDetail.mockData'

export type SeatUtilizationDetailStatus =
  | 'On target'
  | 'Below target'
  | 'Above target'

export type SeatUtilizationDetailRow = {
  id: string
  operatory: string
  utilizationPct: string
  chairHours: string
  peakBlock: string
  status: SeatUtilizationDetailStatus
}

const SEAT_DETAIL_FIGMA_1D: SeatUtilizationDetailRow[] = [
  {
    id: '1',
    operatory: 'Operatory 1',
    utilizationPct: '71%',
    chairHours: '6h',
    peakBlock: '2–4 PM',
    status: 'On target',
  },
  {
    id: '2',
    operatory: 'Operatory 2',
    utilizationPct: '78%',
    chairHours: '7h',
    peakBlock: '2–4 PM',
    status: 'On target',
  },
  {
    id: '3',
    operatory: 'Operatory 3',
    utilizationPct: '85%',
    chairHours: '8h',
    peakBlock: '2–4 PM',
    status: 'Below target',
  },
  {
    id: '4',
    operatory: 'Operatory 4',
    utilizationPct: '92%',
    chairHours: '9h',
    peakBlock: '2–4 PM',
    status: 'Above target',
  },
  {
    id: '5',
    operatory: 'Hygiene 1',
    utilizationPct: '71%',
    chairHours: '10h',
    peakBlock: '2–4 PM',
    status: 'Below target',
  },
  {
    id: '6',
    operatory: 'Hygiene 2',
    utilizationPct: '78%',
    chairHours: '11h',
    peakBlock: '2–4 PM',
    status: 'Above target',
  },
]

export function getSeatUtilizationDetailMetrics(
  range: RangeKey,
): DashboardMetricItemData[] {
  const s = getOperationsSeatUtilizationMetrics(range)
  return [
    {
      id: 'seat-total-overhead',
      leftLabel: 'Total overhead',
      value: s.totalOverhead,
    },
    {
      id: 'seat-expense-ratio',
      leftLabel: 'Expense ratio',
      value: s.expenseRatio,
    },
    {
      id: 'seat-avg-util',
      leftLabel: 'Avg. seat utilization',
      value: s.avgSeatUtilization,
    },
    {
      id: 'seat-peak-util',
      leftLabel: 'Peak seat utilization',
      value: s.peakSeatUtilization,
    },
  ]
}

export function getSeatUtilizationDetailTableData(
  range: RangeKey,
): SeatUtilizationDetailRow[] {
  if (range === '1D') return SEAT_DETAIL_FIGMA_1D
  return SEAT_DETAIL_FIGMA_1D.map((row, i) => ({
    ...row,
    id: `seat-${range}-${i}`,
    utilizationPct: `${68 + ((i * 5) % 28)}%`,
    chairHours: `${32 + i * 8}h`,
    peakBlock: 'Tue / Thu',
  }))
}

const SEAT_LABELS = ['Op 1', 'Op 2', 'Op 3', 'Op 4', 'Hyg 1', 'Hyg 2']

/** Utilization % by seat — 1D matches Figma bar heights. */
const SEAT_BY_OP: Record<RangeKey, SchedulingChartConfig> = {
  '1D': {
    data: [
      { label: 'Op 1', value: 71 },
      { label: 'Op 2', value: 78 },
      { label: 'Op 3', value: 85 },
      { label: 'Op 4', value: 92 },
      { label: 'Hyg 1', value: 71 },
      { label: 'Hyg 2', value: 78 },
    ],
    yDomain: [0, 100],
    yTicks: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
  },
  '1W': {
    data: SEAT_LABELS.map((label, i) => ({
      label,
      value: 74 + i * 2,
      isCurrent: i === 4,
    })),
    yDomain: [0, 100],
    yTicks: [0, 25, 50, 75, 100],
  },
  '1M': {
    data: SEAT_LABELS.map((label, i) => ({
      label,
      value: 76 + (i % 4) * 4,
      isCurrent: i === 0,
    })),
    yDomain: [0, 100],
    yTicks: [0, 25, 50, 75, 100],
  },
  '1Y': {
    data: SEAT_LABELS.map((label, i) => ({
      label,
      value: 77 + (i % 3) * 3,
      isCurrent: i === 5,
    })),
    yDomain: [0, 100],
    yTicks: [0, 25, 50, 75, 100],
  },
  YTD: {
    data: SEAT_LABELS.map((label, i) => ({
      label,
      value: 75 + i * 2,
      isCurrent: i === 1,
    })),
    yDomain: [0, 100],
    yTicks: [0, 25, 50, 75, 100],
  },
}

export function getSeatUtilizationByOperatoryChartConfig(
  range: RangeKey,
): SchedulingChartConfig {
  return SEAT_BY_OP[range]
}

const SEAT_CATEGORY_COLORS: Record<string, string> = {
  Payroll: 'var(--blue-700)',
  Maintenance: 'var(--blue-500)',
  Supplies: 'var(--blue-400)',
  Marketing: 'var(--blue-300)',
  Lab: 'var(--orange-300)',
  'Service fee': 'var(--orange-400)',
}

export function getSeatUtilizationExpenseCategoryChartConfig(
  range: RangeKey,
): ExpenseCategoryBarConfig {
  const nudge: Record<RangeKey, number> = {
    '1D': 0,
    '1W': -1,
    '1M': 0.5,
    '1Y': -0.5,
    YTD: 0.2,
  }
  const data = OPERATIONS_SEAT_DONUT_SLICES.map((s) => ({
    label: s.name,
    value: Math.max(4, Math.round((s.value + nudge[range]) * 10) / 10),
    color: SEAT_CATEGORY_COLORS[s.name] ?? s.color,
  }))

  return {
    data,
    yDomain: [0, 35],
    yTicks: [0, 5, 10, 15, 20, 25, 30, 35],
  }
}
